import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { waLink } from './analytics';
import './Consultation.css';

function Consultation() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const whatsappHref = waLink('Halo, saya ingin konsultasi mengenai layanan Cakra Digital Innovation.');

  const email = 'cdiyunoru@gmail.com';
  const emailSubject = encodeURIComponent('Konsultasi Digital');
  const emailBody = encodeURIComponent(
    'Halo Tim Cakra Digital Innovation,\n\nSaya ingin konsultasi mengenai kebutuhan digital saya.\n\nTerima kasih.'
  );
  const emailHref = `mailto:${email}?subject=${emailSubject}&body=${emailBody}`;

  const consultationSteps = [
    {
      step: 1,
      title: 'Discovery & Analysis',
      description: 'Deep dive into business requirements, market analysis, and technical feasibility assessment.',
      icon: '🔍'
    },
    {
      step: 2,
      title: 'Strategy & Architecture',
      description: 'Enterprise-grade strategic planning with scalable architecture design and technology stack selection.',
      icon: '📋'
    },
    {
      step: 3,
      title: 'Implementation & Deployment',
      description: 'Agile development methodology with enterprise deployment protocols and quality assurance.',
      icon: '⚙️'
    },
    {
      step: 4,
      title: 'Optimization & Scale',
      description: 'Performance monitoring, continuous optimization, and strategic scaling for enterprise growth.',
      icon: '🚀'
    }
  ];

  const services = [
    {
      icon: '🌐',
      title: 'Enterprise Web Solutions',
      description: 'Custom enterprise-grade web applications with scalable architecture and advanced security protocols.',
      features: ['Custom Architecture', 'Enterprise Security', 'Cloud Integration', 'API Development'],
      color: '#3b82f6'
    },
    {
      icon: '⚙️',
      title: 'Software Integration',
      description: 'Enterprise software deployment, system integration, and workflow automation solutions.',
      features: ['System Architecture', 'Data Migration', 'Process Automation', 'API Integration'],
      color: '#8b5cf6'
    },
    {
      icon: '📱',
      title: 'Mobile Applications',
      description: 'Native and cross-platform mobile applications with enterprise-grade performance and security.',
      features: ['Native Development', 'Cross-Platform', 'Enterprise Security', 'Cloud Sync'],
      color: '#10b981'
    },
    {
      icon: '☁️',
      title: 'Cloud Solutions',
      description: 'Enterprise cloud infrastructure, DevOps implementation, and scalable cloud architecture.',
      features: ['Cloud Architecture', 'DevOps Pipeline', 'Security Compliance', '24/7 Monitoring'],
      color: '#f59e0b'
    }
  ];

  // Benefits are now integrated into miniBenefits array

  const miniBenefits = [
    '✓ Free Initial Consultation',
    '✓ 24h Response Guarantee',
    '✓ Enterprise-Ready Solutions',
    '✓ Dedicated Expert Team',
    '✓ ROI-Focused Approach'
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          service_type: formData.service,
          message: formData.message
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          service: '',
          message: ''
        });
        console.log('Contact submitted successfully:', result);
      } else {
        setErrors({ submit: result.error || 'Failed to submit. Please try again.' });
      }
      
    } catch (error) {
      console.error('Submission error:', error);
      setErrors({ submit: 'Failed to submit. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Hero Section */}
      <section className="consultation-hero">
        <div className="consultation-hero__container">
          <div className="consultation-hero__content">
            <span className="consultation-hero__badge">Digital Agency</span>
            <h1 className="consultation-hero__title">Strategi Digital. Solusi Enterprise. Transformasi Bisnis.</h1>
            <p className="consultation-hero__subtitle">
              Enterprise digital transformation partner. We deliver scalable solutions with proven methodologies 
              and dedicated support for sustainable business growth.
            </p>
            <div className="consultation-hero__stats">
              <div className="consultation-hero__stat">
                <div className="consultation-hero__stat-number">3+</div>
                <div className="consultation-hero__stat-label">Proyek Berhasil</div>
              </div>
              <div className="consultation-hero__stat">
                <div className="consultation-hero__stat-number">100%</div>
                <div className="consultation-hero__stat-label">Kepuasan Klien</div>
              </div>
              <div className="consultation-hero__stat">
                <div className="consultation-hero__stat-number">1+</div>
                <div className="consultation-hero__stat-label">Tahun Pengalaman</div>
              </div>
            </div>
            <div className="consultation-hero__actions">
              <a className="btn btn-primary btn-large" href={whatsappHref} target="_blank" rel="noreferrer" aria-label="Konsultasi via WhatsApp">
                <span className="btn__icon">📱</span>
                <span>Start Consultation</span>
              </a>
              <a className="btn btn-outline btn-large" href={emailHref} target="_blank" rel="noreferrer" aria-label="Schedule Executive Meeting">
                <span className="btn__icon">📅</span>
                <span>Schedule Executive Meeting</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Consultation Section */}
      <section className="consultation-main">
        <div className="consultation-main__container">
          <div className="consultation-main__grid">
            {/* Left Column - Information */}
            <div className="consultation-main__info">
              <div className="consultation-main__header">
                <h2 className="consultation-main__title">Transform Your Business with Enterprise Solutions</h2>
                <p className="consultation-main__subtitle">
                  Partner with us to unlock your digital potential. Our proven methodologies and enterprise-grade 
                  solutions ensure measurable results and sustainable growth for your business.
                </p>
              </div>

              {/* Mini Benefits */}
              <div className="consultation-main__benefits">
                <h3 className="consultation-main__benefits-title">Why Choose Us</h3>
                <div className="consultation-main__benefits-list">
                  {miniBenefits.map((benefit, index) => (
                    <div key={index} className="consultation-main__benefit-item">
                      <span className="consultation-main__benefit-text">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Services Preview */}
              <div className="consultation-main__services">
                <h3 className="consultation-main__services-title">Our Expertise</h3>
                <div className="consultation-main__services-grid">
                  {services.slice(0, 2).map((service, index) => (
                    <div key={index} className="consultation-main__service-card">
                      <div className="consultation-main__service-icon" style={{ backgroundColor: `${service.color}20`, color: service.color }}>
                        {service.icon}
                      </div>
                      <h4 className="consultation-main__service-title">{service.title}</h4>
                      <p className="consultation-main__service-description">{service.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Testimonial Snippet */}
              <div className="consultation-main__testimonial">
                <div className="consultation-main__testimonial-content">
                  <div className="consultation-main__testimonial-icon">💬</div>
                  <blockquote className="consultation-main__testimonial-text">
                    "Cakra Digital Innovation transformed our digital presence completely. Their enterprise solutions 
                    and dedicated support exceeded our expectations."
                  </blockquote>
                  <cite className="consultation-main__testimonial-author">
                    <strong>CEO, Tech Startup</strong>
                  </cite>
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="consultation-main__form-section">
              <div className="consultation-main__form-card">
                <div className="consultation-main__form-header">
                  <h3 className="consultation-main__form-title">Start Your Digital Transformation</h3>
                  <p className="consultation-main__form-subtitle">
                    Fill out the form below and our team will contact you within 24 hours
                  </p>
                </div>

                {isSubmitted ? (
                  <div className="consultation-main__success">
                    <div className="consultation-main__success-icon">✅</div>
                    <h4 className="consultation-main__success-title">Thank You!</h4>
                    <p className="consultation-main__success-message">
                      Your consultation request has been received. Our team will contact you within 24 hours.
                    </p>
                    <button 
                      className="btn btn-primary"
                      onClick={() => setIsSubmitted(false)}
                    >
                      Submit Another Request
                    </button>
                  </div>
                ) : (
                  <form className="consultation-main__form" onSubmit={handleSubmit}>
                    <div className="consultation-main__form-row">
                      <div className="consultation-main__form-group">
                        <label htmlFor="name" className="consultation-main__form-label">Full Name *</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          className={`consultation-main__form-input ${errors.name ? 'error' : ''}`}
                          placeholder="Enter your full name"
                          value={formData.name}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                        />
                        {errors.name && <span className="consultation-main__form-error">{errors.name}</span>}
                      </div>
                      <div className="consultation-main__form-group">
                        <label htmlFor="email" className="consultation-main__form-label">Email Address *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          className={`consultation-main__form-input ${errors.email ? 'error' : ''}`}
                          placeholder="your@email.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                        />
                        {errors.email && <span className="consultation-main__form-error">{errors.email}</span>}
                      </div>
                    </div>

                    <div className="consultation-main__form-row">
                      <div className="consultation-main__form-group">
                        <label htmlFor="company" className="consultation-main__form-label">Company Name</label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          className="consultation-main__form-input"
                          placeholder="Your company name"
                          value={formData.company}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="consultation-main__form-group">
                        <label htmlFor="phone" className="consultation-main__form-label">Phone Number</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          className="consultation-main__form-input"
                          placeholder="+62 8xx-xxxx-xxxx"
                          value={formData.phone}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>

                    <div className="consultation-main__form-group">
                      <label htmlFor="service" className="consultation-main__form-label">Service Interest</label>
                      <select
                        id="service"
                        name="service"
                        className="consultation-main__form-select"
                        value={formData.service}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                      >
                        <option value="">Select a service</option>
                        <option value="web">Enterprise Web Solutions</option>
                        <option value="software">Software Integration</option>
                        <option value="mobile">Mobile Applications</option>
                        <option value="cloud">Cloud Solutions</option>
                        <option value="consulting">Digital Consulting</option>
                      </select>
                    </div>

                    <div className="consultation-main__form-group">
                      <label htmlFor="message" className="consultation-main__form-label">Project Details *</label>
                      <textarea
                        id="message"
                        name="message"
                        className={`consultation-main__form-textarea ${errors.message ? 'error' : ''}`}
                        placeholder="Tell us about your project requirements and goals..."
                        rows={5}
                        value={formData.message}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                      />
                      {errors.message && <span className="consultation-main__form-error">{errors.message}</span>}
                    </div>

                    {errors.submit && (
                      <div className="consultation-main__form-error-global">
                        {errors.submit}
                      </div>
                    )}

                    <button
                      type="submit"
                      className="consultation-main__form-submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="consultation-main__form-spinner"></span>
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <>
                          <span className="consultation-main__form-submit-icon">🚀</span>
                          <span>Start Consultation</span>
                        </>
                      )}
                    </button>
                  </form>
                )}

                <div className="consultation-main__form-footer">
                  <p className="consultation-main__form-footer-text">
                    Prefer to talk directly? 
                    <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="consultation-main__form-footer-link">
                      Chat with us on WhatsApp
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="consultation-process">
        <div className="consultation-process__container">
          <div className="consultation-process__header">
            <h2 className="consultation-process__title">Our Enterprise Process</h2>
            <p className="consultation-process__subtitle">Proven methodology for enterprise digital transformation</p>
          </div>
          <div className="consultation-process__timeline">
            {consultationSteps.map((step, index) => (
              <div key={step.step} className="consultation-process__step">
                <div className="consultation-process__step-number">{step.step}</div>
                <div className="consultation-process__step-icon">{step.icon}</div>
                <h3 className="consultation-process__step-title">{step.title}</h3>
                <p className="consultation-process__step-description">{step.description}</p>
                {index < consultationSteps.length - 1 && <div className="consultation-process__step-connector"></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="consultation-cta">
        <div className="consultation-cta__container">
          <div className="consultation-cta__content">
            <h2 className="consultation-cta__title">Ready for Enterprise Transformation?</h2>
            <p className="consultation-cta__subtitle">
              Let's discuss how our enterprise solutions can drive your business growth and competitive advantage.
            </p>
            <div className="consultation-cta__stats">
              <div className="consultation-cta__stat">
                <div className="consultation-cta__stat-number">3+</div>
                <div className="consultation-cta__stat-label">Bisnis Transformasi</div>
              </div>
              <div className="consultation-cta__stat">
                <div className="consultation-cta__stat-number">98%</div>
                <div className="consultation-cta__stat-label">Kepuasan Klien</div>
              </div>
              <div className="consultation-cta__stat">
                <div className="consultation-cta__stat-number">24/7</div>
                <div className="consultation-cta__stat-label">Support</div>
              </div>
            </div>
            <div className="consultation-cta__actions">
              <Link to="/pricing" className="btn btn-primary btn-large">
                <span className="btn__icon">📋</span>
                <span>View Enterprise Pricing</span>
              </Link>
              <a className="btn btn-secondary btn-large" href={whatsappHref} target="_blank" rel="noopener noreferrer">
                <span className="btn__icon">📱</span>
                <span>Start Executive Consultation</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Consultation;
